import { ref } from "@vue/composition-api";
import autoEmailService from "@/services/autoEmailReport.service";

// Singleton partagé au niveau module pour persister le polling inter-onglets
const sharedSyncing = ref(false);
let syncPollTimer = null;
let syncPollActive = false;
let pollErrorCount = 0;
const MAX_POLL_ERRORS = 5;
const subscribers = new Set();

const notifySubscribers = (type, payload) => {
  subscribers.forEach((sub) => {
    try {
      // type "status" signale un rafraîchissement d'état, on notifie les abonnés pour actualiser l'historique et le statut
      if (type === "status") {
        if (sub.onStatus) sub.onStatus(payload);
      }
      if (type === "success") {
        if (sub.notify) sub.notify("Synchronisation GSS terminée avec succès !", "success");
        if (sub.onSuccess) sub.onSuccess(payload);
      }
      if (type === "error") {
        if (sub.notify) sub.notify(payload || "Échec de la synchro GSS", "error");
        if (sub.onError) sub.onError(payload);
      }
      if (type === "started") {
        if (sub.notify) sub.notify(payload || "Synchronisation GSS lancée en arrière-plan", "info");
        if (sub.onSyncStarted) sub.onSyncStarted(payload);
      }
    } catch (e) {
      console.error("Erreur notification subscriber useGssSync :", e);
    }
  });
};

const poll = async () => {
  if (!syncPollActive) return;
  try {
    const statusRes = await autoEmailService.getSyncStatus();
    pollErrorCount = 0;
    if (!statusRes?.data?.is_syncing) {
      syncPollActive = false;
      sharedSyncing.value = false;
      if (statusRes?.data?.error) {
        notifySubscribers("error", `Échec de la synchro GSS : ${statusRes.data.error}`);
      } else {
        notifySubscribers("success", statusRes?.data);
      }
      notifySubscribers("status", statusRes?.data);
    } else {
      sharedSyncing.value = true;
      notifySubscribers("status", statusRes?.data);
      if (syncPollTimer) clearTimeout(syncPollTimer);
      syncPollTimer = setTimeout(poll, 3000);
    }
  } catch (e) {
    console.error("Erreur polling status synchro :", e);
    pollErrorCount++;
    if (pollErrorCount >= MAX_POLL_ERRORS) {
      syncPollActive = false;
      sharedSyncing.value = false;
      notifySubscribers("error", "Synchronisation interrompue suite à des erreurs réseau répétées.");
      notifySubscribers("status", { is_syncing: false, error: "max_errors_exceeded" });
      return;
    }
    if (syncPollActive) {
      if (syncPollTimer) clearTimeout(syncPollTimer);
      syncPollTimer = setTimeout(poll, 3000);
    }
  }
};

const startPolling = () => {
  if (syncPollActive) return;
  if (syncPollTimer) clearTimeout(syncPollTimer);
  pollErrorCount = 0;
  syncPollActive = true;
  poll();
};

export function useGssSync(notifyOrOptions, onSuccess, onSyncStarted, onStatus, onError) {
  let subscriber;
  if (
    notifyOrOptions &&
    typeof notifyOrOptions === "object" &&
    typeof notifyOrOptions !== "function"
  ) {
    subscriber = notifyOrOptions;
  } else {
    subscriber = {
      notify: notifyOrOptions,
      onSuccess,
      onSyncStarted,
      onStatus,
      onError,
    };
  }
  subscribers.add(subscriber);

  const checkSyncStatus = async () => {
    try {
      const statusRes = await autoEmailService.getSyncStatus();
      if (statusRes?.data?.is_syncing) {
        sharedSyncing.value = true;
        startPolling();
      } else {
        sharedSyncing.value = false;
      }
    } catch (e) {
      console.warn("Impossible de vérifier le statut initial de la synchro GSS", e);
    }
  };

  const syncSpreadsheetData = async (spreadsheetUrl = null, datasetName = null) => {
    if (sharedSyncing.value) return;
    sharedSyncing.value = true;
    try {
      const res = await autoEmailService.syncSpreadsheet(spreadsheetUrl, datasetName);
      notifySubscribers("started", res?.data?.message);
      startPolling();
    } catch (err) {
      sharedSyncing.value = false;
      notifySubscribers("error", "Erreur lors de la synchronisation GSS");
      notifySubscribers("status", { is_syncing: false, error: err });
    }
  };

  const stopSyncPolling = () => {
    subscribers.delete(subscriber);
    if (subscribers.size === 0) {
      syncPollActive = false;
      pollErrorCount = 0;
      if (syncPollTimer) {
        clearTimeout(syncPollTimer);
        syncPollTimer = null;
      }
    }
  };

  return {
    syncingSpreadsheet: sharedSyncing,
    syncSpreadsheetData,
    checkSyncStatus,
    stopSyncPolling,
  };
}
